import { Box, Button, Container, Flex, Grid, Group, Pagination, Stack, Text, Title } from '@mantine/core';
import { PlusIcon } from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';

import { SubMenuItem } from '../../components/global/SubMenuItem/SubMenuItem.tsx';
import { useEventsCountQuery } from '../../data/events/useEventsCountQuery.ts';
import { useFilteredEventsCountQuery } from '../../data/events/useFilteredEventsCountQuery.ts';
import { usePaginatedEventsQuery } from '../../data/events/usePaginatedEventsQuery.ts';
import type { Timeframe } from '../../data/events/usePaginatedEventsQuery.ts';
import { useFilteredIncompleteEventsCountQuery } from '../../data/incompleteEvents/useFilteredIncompleteEventsCountQuery.ts';
import { useIncompleteEventsCountQuery } from '../../data/incompleteEvents/useIncompleteEventsCountQuery.ts';
import { usePaginatedIncompleteEventsQuery } from '../../data/incompleteEvents/usePaginatedIncompleteEventsQuery.ts';
import { useI18n } from '../../hooks/useI18n.ts';
import { notEmpty } from '../../lib/notEmpty.ts';
import { EventDelete } from '../eventDelete/EventDelete.tsx';
import { useIncompleteEventItems } from '../hooks/useIncompleteEventItems.ts';
import { IncompleteEventDelete } from '../incompleteEventDelete/IncompleteEventDelete.tsx';
import { SortFilterMenu } from './components/SortFilterMenu.tsx';
import { TimeframeToggle } from './components/TimeframeToggle.tsx';
import { parseEvents } from './lib/parseEvent.ts';
import { useDashboardParams } from './lib/useDashboardParams.ts';
import { useEventGrid } from './lib/useEventGrid.tsx';

const ITEMS_PER_PAGE = 12;

const NoItemsPanel: FC<{ message: string; children?: ReactNode }> = ({ message, children }) => (
  <Box bg="white" mt="xxs" py={100} style={{ textAlign: 'center', borderRadius: 'var(--mantine-radius-sm)' }} w="100%">
    <Text c="gray.5" size="lg">
      {message}
    </Text>
    {children}
  </Box>
);

export const Dashboard: FC = () => {
  const { status = 'published' } = useParams<{
    status: 'published' | 'pending' | 'incomplete' | 'declined';
  }>();
  const [deleteModalData, setDeleteModalData] = useState<{
    entityType: 'incomplete-event' | 'event';
    id?: string;
    deleteAll?: boolean;
  } | null>(null);
  const navigate = useNavigate();

  const { t } = useI18n();

  // Filters, sorting, timeframe and page all live in the query string, so a view can be linked
  // and survives a reload. Defaults to upcoming: past events are what clutter the published tab.
  const { sort, categoryId, eventType, timeframe, page, setParams, clearFilters } = useDashboardParams();

  // Tab badge counts (unfiltered)
  const { data: publishedCount, isPending: publishedCountPending } = useEventsCountQuery({ status: 'published' });
  const { data: pendingCount, isPending: pendingCountPending } = useEventsCountQuery({ status: 'created' });
  const { data: incompleteCount, isPending: incompleteCountPending } = useIncompleteEventsCountQuery();
  const { data: declinedCount, isPending: declinedCountPending } = useEventsCountQuery({ status: 'declined' });

  // Map tab status to Directus status
  const directusStatus = status === 'pending' ? 'created' : (status as 'published' | 'declined');

  // Pending, declined and incomplete events are never "past" in a useful sense, so the
  // timeframe only narrows the published tab.
  const activeTimeframe: Timeframe = status === 'published' ? timeframe : 'all';

  // Paginated data for active tab
  const { data: paginatedEvents } = usePaginatedEventsQuery({
    status: directusStatus,
    page,
    limit: ITEMS_PER_PAGE,
    sort,
    categoryId,
    eventType,
    timeframe: activeTimeframe,
    enabled: status !== 'incomplete',
  });

  const { data: paginatedIncompleteEvents } = usePaginatedIncompleteEventsQuery({
    page,
    limit: ITEMS_PER_PAGE,
    sort,
    categoryId,
    eventType,
    enabled: status === 'incomplete',
  });

  // Filtered counts for pagination
  const { data: filteredEventsCount } = useFilteredEventsCountQuery({
    status: directusStatus,
    categoryId,
    eventType,
    timeframe: activeTimeframe,
    enabled: status !== 'incomplete',
  });

  // Counts for the two sides of the toggle. They respect the other active filters, so each
  // number is what you would actually see after clicking.
  const { data: upcomingCount } = useFilteredEventsCountQuery({
    status: 'published',
    categoryId,
    eventType,
    timeframe: 'upcoming',
    enabled: status === 'published',
  });

  const { data: allCount } = useFilteredEventsCountQuery({
    status: 'published',
    categoryId,
    eventType,
    timeframe: 'all',
    enabled: status === 'published',
  });

  const { data: filteredIncompleteCount } = useFilteredIncompleteEventsCountQuery({
    categoryId,
    eventType,
    enabled: status === 'incomplete',
  });

  // Parse events for the active tab
  const statusForParsing = status === 'pending' ? 'pending' : status === 'incomplete' ? 'draft' : status;
  const activeEvents = useMemo(() => {
    if (status === 'incomplete') {
      return parseEvents(paginatedIncompleteEvents, 'draft');
    }
    return parseEvents(paginatedEvents as unknown as Parameters<typeof parseEvents>[0], statusForParsing);
  }, [status, paginatedEvents, paginatedIncompleteEvents, statusForParsing]);

  // Pagination
  const totalCount = status === 'incomplete' ? (filteredIncompleteCount ?? 0) : (filteredEventsCount ?? 0);
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const { createMutation } = useIncompleteEventItems();

  const onCreate = useCallback(() => {
    createMutation.mutate(undefined, {
      onSuccess: (id) => {
        if (!id) {
          return;
        }

        void navigate(`/incomplete-event/${id}/create`);
      },
    });
  }, [createMutation, navigate]);

  const onDropdownClick = useCallback(
    ({
      state,
      id,
      entityType,
    }: {
      state: 'edit' | 'remove' | 'remove-all';
      id?: string;
      entityType: 'incomplete-event' | 'event';
    }) => {
      if (state === 'remove') {
        setDeleteModalData({ entityType, id });
        return;
      }

      if (state === 'remove-all') {
        setDeleteModalData({ entityType, id, deleteAll: true });
        return;
      }

      const pathParts = [entityType, id, state];
      const pathPartsFiltered = pathParts.filter(notEmpty);

      if (pathPartsFiltered.length !== pathParts.length) {
        return;
      }

      void navigate(`/${pathParts.join('/')}`);
    },
    [navigate],
  );

  const entityType = status === 'incomplete' ? 'incomplete-event' : 'event';
  const basePath = status === 'incomplete' ? '/incomplete-event' : '/event';

  const hasActiveFilters = !!categoryId || !!eventType;

  const clearAllFilters = clearFilters;

  const noItemsMessage = hasActiveFilters
    ? (t('dashboard_message--no-filtered-results') ?? 'No events match your filters.')
    : {
        published: t('dashboard_message--no-published-events') ?? 'No published events available.',
        pending: t('dashboard_message--no-pending-events') ?? 'No pending events available.',
        incomplete: t('dashboard_message--no-incomplete-events') ?? 'No incomplete events available.',
        declined: t('dashboard_message--no-declined-events') ?? 'No declined events.',
      }[status];

  const eventGrid = useEventGrid({
    events: activeEvents,
    entityType,
    basePath,
    onDropdownClick,
    emptyContent: (
      <NoItemsPanel message={noItemsMessage}>
        {hasActiveFilters ? (
          <Button color="dark.9" mt="md" radius="xl" size="lg" variant="outline" onClick={clearAllFilters}>
            {t('dashboard_button--clear-filters') ?? 'Clear filters'}
          </Button>
        ) : (
          status === 'incomplete' && (
            <Button
              color="dark.9"
              loading={createMutation.isPending}
              mt="md"
              radius="xl"
              size="lg"
              variant="outline"
              onClick={onCreate}
            >
              <Group gap="xxxs">
                <PlusIcon color="black" size="28" /> {t('dashboard_button--create-event') ?? 'Create event'}
              </Group>
            </Button>
          )
        )}
      </NoItemsPanel>
    ),
  });

  return (
    <>
      {deleteModalData?.entityType === 'event' && (
        <EventDelete
          deleteAll={deleteModalData?.deleteAll}
          eventId={deleteModalData?.id}
          onClose={() => setDeleteModalData(null)}
        />
      )}
      {deleteModalData?.entityType === 'incomplete-event' && (
        <IncompleteEventDelete incompleteEventId={deleteModalData?.id} onClose={() => setDeleteModalData(null)} />
      )}

      <Container py={50} size="lg">
        <Stack gap="xs">
          <Stack gap="md">
            <Flex align="center" justify="space-between">
              <Title order={2}>{t('dashboard_title--your-events') ?? 'Your events'}</Title>
              <Box>
                <Button
                  color="dark.9"
                  loading={createMutation.isPending}
                  radius="xl"
                  size="xl"
                  styles={{ label: { fontSize: 20 } }}
                  onClick={onCreate}
                >
                  <Group gap="xxxs">
                    <PlusIcon color="#D060E2" size="28" /> {t('dashboard_button--create-event') ?? 'Create event'}
                  </Group>
                </Button>
              </Box>
            </Flex>
            <Flex align="center" justify="space-between" wrap="wrap">
              <Group gap="xs">
                <SubMenuItem
                  isActive={status === 'published'}
                  isLoading={publishedCountPending}
                  label={t('dashboard_submenu--published') ?? 'Published'}
                  noOfItems={publishedCount}
                  to="../published"
                />
                <SubMenuItem
                  isActive={status === 'pending'}
                  isLoading={pendingCountPending}
                  label={t('dashboard_submenu--pending') ?? 'Pending'}
                  noOfItems={pendingCount}
                  to="../pending"
                />
                <SubMenuItem
                  isActive={status === 'incomplete'}
                  isLoading={incompleteCountPending}
                  label={t('dashboard_submenu--incomplete') ?? 'Incomplete Events'}
                  noOfItems={incompleteCount}
                  to="../incomplete"
                />
                <SubMenuItem
                  isActive={status === 'declined'}
                  isLoading={declinedCountPending}
                  label={t('dashboard_submenu--declined') ?? 'Declined'}
                  noOfItems={declinedCount}
                  to="../declined"
                />
              </Group>
              <SortFilterMenu
                categoryId={categoryId}
                eventType={eventType}
                sort={sort}
                onApply={setParams}
                onClearAll={clearAllFilters}
              />
            </Flex>

            {status === 'published' && (
              <TimeframeToggle
                counts={{ all: allCount, upcoming: upcomingCount }}
                labels={{
                  all: t('dashboard_timeframe--all') ?? 'All',
                  upcoming: t('dashboard_timeframe--upcoming') ?? 'Upcoming',
                }}
                value={timeframe}
                onChange={(next) => setParams({ timeframe: next })}
              />
            )}
          </Stack>
          <Box>
            <Grid gap="xs">{eventGrid}</Grid>
            {totalPages > 1 && (
              <Flex justify="center" mt="md">
                <Pagination
                  color="dark.9"
                  radius="xl"
                  total={totalPages}
                  value={page}
                  onChange={(next) => setParams({ page: next })}
                />
              </Flex>
            )}
          </Box>
        </Stack>
      </Container>
    </>
  );
};
